import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { TAB_ITEMS, workoutSessionPath, type RouteKey } from './routes';
import { UpdateRequired } from '../../app/UpdateRequired';
import { sessionRepository } from '../../data/repositories/sessionRepository';
import type { SessionRecord } from '../../data/types';
import styles from './AppShell.module.css';

// Nudged down from 22 — bottom nav refinement pass: a touch lighter and
// less dominant while still easy to recognise at a glance.
const ICON_PROPS = {
  width: 19,
  height: 19,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

/** Presentation only — one outline glyph per tab, keyed by route. */
const TAB_ICONS: Partial<Record<RouteKey, React.JSX.Element>> = {
  dashboard: (
    <svg {...ICON_PROPS}>
      <path d="M4 11l8-7 8 7v9H4z" />
    </svg>
  ),
  plans: (
    <svg {...ICON_PROPS}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 9h6M9 13h6M9 17h3" />
    </svg>
  ),
  exercises: (
    <svg {...ICON_PROPS}>
      <path d="M3 9v6M6 7v10M18 7v10M21 9v6M6 12h12" />
    </svg>
  ),
  progress: (
    <svg {...ICON_PROPS}>
      <path d="M4 19V5M4 19h16M8 15l3-4 3 2 5-6" />
    </svg>
  ),
  settings: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    </svg>
  ),
};

/**
 * A thin bar above the tab bar, on every tab, when a workout is open.
 * Master Phase 8 (§13 of the spec names this alongside Progress's
 * sub-screens). Shares its data dependency with the Dashboard resume card —
 * both read `sessionRepository.getOpenSession()`, scoped to `active`/
 * `paused` (the same scope the Workout tab itself uses).
 */
function ActiveWorkoutBar(): React.JSX.Element | null {
  const location = useLocation();
  const [openSession, setOpenSession] = useState<SessionRecord | null>(null);

  useEffect(() => {
    let cancelled = false;
    void sessionRepository.getOpenSession().then((session) => {
      if (!cancelled) setOpenSession(session ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (openSession === null) return null;
  // Already on the session's own execution screen — the bar would be redundant.
  if (location.pathname === workoutSessionPath(openSession.id)) return null;

  return (
    <Link to={workoutSessionPath(openSession.id)} className={styles.activeWorkoutBar}>
      <span className={styles.activeWorkoutDot} aria-hidden="true" />
      {openSession.name ?? 'Workout'} in progress — tap to resume
    </Link>
  );
}

/**
 * The application shell: the routed screen and a bottom tab bar.
 *
 * Semantic landmarks (`main`, `nav`) rather than styled divs, so assistive
 * technology gets the structure for free — Phase 0 §16.
 *
 * Logo redesign pass: a top-left brand mark briefly lived here (a
 * `LiftXStackedLogo` in a square header) — superseded by a later revision of
 * the same spec, which moved the logged-in app's one prominent logo onto the
 * Home page itself (centred, large) and removed it from every other
 * logged-in screen entirely, this shell included.
 */
export function AppShell(): React.JSX.Element {
  const { pathname } = useLocation();
  // Same "is this tab current" rule as NavLink (`end` for Home, prefix for the
  // rest), used only to position the sliding highlight — NavLink still owns
  // `aria-current`.
  const activeIndex = TAB_ITEMS.findIndex((item) =>
    item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`),
  );
  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main">
        Skip to content
      </a>

      <UpdateRequired />

      <main id="main" className={styles.main} tabIndex={-1}>
        <Outlet />
      </main>

      <ActiveWorkoutBar />

      <nav className={styles.tabBar} aria-label="Primary">
        <ul
          className={styles.tabList}
          style={{ '--n': TAB_ITEMS.length, '--i': Math.max(activeIndex, 0) } as React.CSSProperties}
        >
          {activeIndex >= 0 && <li className={styles.tabPill} role="presentation" aria-hidden="true" />}
          {TAB_ITEMS.map((item) => (
            <li key={item.key} className={styles.tabItem}>
              <NavLink
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab
                }
                // The accessible name is the full label; the visible text is
                // the short one. Colour is never the only active indicator —
                // aria-current carries it too (Phase 0 §16).
                aria-label={item.label}
              >
                {TAB_ICONS[item.key]}
                <span className={styles.tabLabel}>{item.tabLabel}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
